import { useTranslation } from 'react-i18next';
import styles from './PinField.module.css';

/** Six digits, and the only length this app's PINs ever have — see `auth/pin.ts`. */
const PIN_LENGTH = 6;

/**
 * A six-digit PIN entry.
 *
 * The details here are the whole point of the component, and every one of them
 * is about the person this feature exists for — someone on a phone who does not
 * want an account:
 *
 *   `inputMode="numeric"`  brings up the number pad rather than a full
 *                          keyboard, on both iOS and Android.
 *   non-digits stripped    pasting "123 456" or a stray letter silently
 *                          becomes what was meant instead of failing on submit.
 *   `type="password"`      so it is not readable over a shoulder in a room full
 *                          of the exact people it keeps out.
 *   `autoComplete="off"`   a browser password manager offering to save a
 *                          six-digit number under this site's origin is noise,
 *                          and would be shared across every guest on the device.
 */
export default function PinField({
  id,
  label,
  value,
  onChange,
  autoFocus = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`input ${styles.pin}`}
        type="password"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        maxLength={PIN_LENGTH}
        value={value}
        placeholder={t('guest.pinPlaceholder')}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
      />
    </div>
  );
}
